import { zodResolver } from "@hookform/resolvers/zod";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid2,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getAdminEntities } from "../../api/entities/getAdminEntities";
import { useErrorHandler, useFormStyles } from "../../hooks";
import { AccountType } from "../../types/account";
import {
  CreateAccountForm,
  getCreateAccountFormSchema,
} from "../../types/forms/admin/account";
import { Role } from "../../types/interfaces/account";
import { Entity } from "../../types/model/entity/Entity";

export const ADD_ACCOUNT_FORM_ID = "add-account-form";

interface AddAccountFormProps {
  availableRoles: Role[];
  onSubmit: (values: CreateAccountForm) => Promise<void>;
}

export default function AddAccountForm({
  onSubmit,
  availableRoles
}: Readonly<AddAccountFormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const formStyles = useFormStyles(true);

  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [entities, setEntities] = useState<Entity[]>([]);

  const validationSchema = getCreateAccountFormSchema(t);
  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: "",
      type: AccountType.STANDARD,
      entityId: undefined,
      email: "",
      phoneNumber: "",
      fiscalNumber: "",
      address: "",
      zipCode: "",
      city: "",
      description: "",
      user: {
        name: "",
        email: "",
        password: "",
        roleId: undefined,
        phoneNumber: "",
      },
      permissions: [],
    },
  });

  const accountType = methods.watch("type");
  const requiresEntity =
    accountType === AccountType.ENTITY || accountType === AccountType.ENTITY_WRITE;

  useEffect(() => {
    if (!requiresEntity) {
      methods.setValue("entityId", null);
      return;
    }

    const loadEntities = async () => {
      try {
        const response = await getAdminEntities();
        setEntities(response);
      } catch (error) {
        handleError(error);
      }
    };

    loadEntities();
  }, [requiresEntity]);

  return (
    <Box sx={{ width: "100%" }}>
      <FormProvider {...methods}>
        <Box
          component="form"
          id={ADD_ACCOUNT_FORM_ID}
          onSubmit={methods.handleSubmit((values) => {

            if (confirmPassword !== methods.getValues("user.password")) {
              return enqueueSnackbar(
                t("admin.users.errors.password-mismatch"),
                { variant: "error" }
              );
            }

            onSubmit(values);
          })}
        >
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              {/* Account */}
              <Typography variant="h5" sx={{ mb: 2, mt: 1 }}>
                {t("admin.accounts.section-title")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    sx={{ ...formStyles }}
                    label={`${t("admin.accounts.account.name")} *`}
                    {...methods.register("name")}
                    error={!!methods.formState.errors.name}
                    helperText={methods.formState.errors.name?.message}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("admin.accounts.account.fiscal-number")}
                    variant="outlined"
                    sx={{ ...formStyles }}
                    {...methods.register("fiscalNumber")}
                    error={!!methods.formState.errors.fiscalNumber}
                    helperText={methods.formState.errors.fiscalNumber?.message}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <Controller
                    name="type"
                    control={methods.control}
                    render={({ field }) => (
                      <TextField
                        select
                        fullWidth
                        label={`${t("admin.accounts.account.type")} *`}
                        variant="outlined"
                        sx={{ ...formStyles }}
                        {...field}
                        error={!!methods.formState.errors.type}
                        helperText={methods.formState.errors.type?.message}
                      >
                        {Object.values(AccountType).map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid2>

                {requiresEntity && (
                  <Grid2 size={6}>
                    <Controller
                      name="entityId"
                      control={methods.control}
                      render={({ field }) => (
                        <TextField
                          select
                          fullWidth
                          label={`${t("admin.accounts.account.entity")} *`}
                          variant="outlined"
                          sx={{ ...formStyles }}
                          {...field}
                          value={field.value ?? ""}
                          error={!!methods.formState.errors.entityId}
                          helperText={methods.formState.errors.entityId?.message}
                        >
                          {entities.map((entity) => (
                            <MenuItem key={entity.id} value={entity.id}>
                              {entity.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid2>
                )}

                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={`${t("admin.accounts.account.email")} *`}
                    variant="outlined"
                    sx={{ ...formStyles }}
                    {...methods.register("email")}
                    error={!!methods.formState.errors.email}
                    helperText={methods.formState.errors.email?.message}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("admin.accounts.account.phone-number")}
                    variant="outlined"
                    sx={{ ...formStyles }}
                    {...methods.register("phoneNumber")}
                    error={!!methods.formState.errors.phoneNumber}
                    helperText={methods.formState.errors.phoneNumber?.message}
                  />
                </Grid2>
                <Grid2 size={7}>
                  <TextField
                    fullWidth
                    label={t("admin.accounts.account.address")}
                    variant="outlined"
                    sx={{ ...formStyles }}
                    {...methods.register("address")}
                    error={!!methods.formState.errors.address}
                    helperText={methods.formState.errors.address?.message}
                  />
                </Grid2>
                <Grid2 size={3}>
                  <TextField
                    fullWidth
                    label={t("admin.accounts.account.city")}
                    variant="outlined"
                    sx={{ ...formStyles }}
                    {...methods.register("city")}
                    error={!!methods.formState.errors.city}
                    helperText={methods.formState.errors.city?.message}
                  />
                </Grid2>
                <Grid2 size={2}>
                  <TextField
                    fullWidth
                    label={t("admin.accounts.account.zip-code")}
                    variant="outlined"
                    sx={{ ...formStyles }}
                    {...methods.register("zipCode")}
                    error={!!methods.formState.errors.zipCode}
                    helperText={methods.formState.errors.zipCode?.message}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    sx={{ ...formStyles }}
                    label={t("admin.accounts.account.description")}
                    placeholder={t(
                      "admin.accounts.account.description-placeholder"
                    )}
                    multiline
                    minRows={6}
                    {...methods.register("description")}
                    error={!!methods.formState.errors.description}
                    helperText={methods.formState.errors.description?.message}
                  />
                </Grid2>
              </Grid2>

              {/* User */}
              <Typography variant="h5" sx={{ mb: 2, mt: 4 }}>
                {t("admin.users.section-title")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={`${t("admin.users.name")} *`}
                    variant="outlined"
                    sx={{ ...formStyles }}
                    {...methods.register("user.name")}
                    error={!!methods.formState.errors.user?.name}
                    helperText={methods.formState.errors.user?.name?.message}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={`${t("admin.users.email")} *`}
                    variant="outlined"
                    sx={{ ...formStyles }}
                    {...methods.register("user.email")}
                    error={!!methods.formState.errors.user?.email}
                    helperText={methods.formState.errors.user?.email?.message}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    label={`${t("admin.users.password")} *`}
                    variant="outlined"
                    sx={{ ...formStyles }}
                    {...methods.register("user.password")}
                    error={!!methods.formState.errors.user?.password}
                    helperText={methods.formState.errors.user?.password?.message}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword((prev) => !prev)}
                              edge="end"
                              aria-label={
                                showPassword
                                  ? t("admin.users.hide-password")
                                  : t("admin.users.show-password")
                              }
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    type={showConfirm ? "text" : "password"}
                    label={`${t("admin.users.password-confirm")} *`}
                    variant="outlined"
                    sx={{ ...formStyles }}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirm((prev) => !prev)}
                              edge="end"
                              aria-label={
                                showConfirm
                                  ? t("admin.users.hide-password")
                                  : t("admin.users.show-password")
                              }
                            >
                              {showConfirm ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label={t("admin.users.phone-number")}
                    variant="outlined"
                    sx={{ ...formStyles }}
                    {...methods.register("user.phoneNumber")}
                    error={!!methods.formState.errors.user?.phoneNumber}
                    helperText={
                      methods.formState.errors.user?.phoneNumber?.message as any
                    }
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Controller
                    name="user.roleId"
                    control={methods.control}
                    render={({ field }) => (
                      <TextField
                        select
                        fullWidth
                        label={`${t("admin.users.role")} *`}
                        variant="outlined"
                        sx={{ ...formStyles }}
                        {...field}
                        error={!!methods.formState.errors.user?.roleId}
                        helperText={
                          methods.formState.errors.user?.roleId?.message as any
                        }
                      >
                        {availableRoles.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            {t(role.i18nLabel)}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid2>
              </Grid2>
            </Box>
          </Paper>
        </Box>
      </FormProvider>
    </Box>
  );
}